import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { Calendar, MapPin, Clock, ChevronRight, Star, ExternalLink } from 'lucide-react';

// Featured Event - AngolaDay 50º Aniversário
const FEATURED_EVENT = {
  id: 'angola-day-50',
  title: 'AngolaDay - 50.º Aniversário da Independência de Angola',
  description: 'A Embaixada de Angola em Espanha convida todos para a celebração do Dia de Angola 2025, comemorando o 50.º Aniversário da Independência Nacional. O evento contará com acto institucional, stands culturais, gastronomia angolana, conciertos, desfiles de carnaval, danças de kizomba e sessões de DJ.',
  date: '2025-11-11',
  time: '15:00 - 21:00',
  location: 'Plaza de Toros de Las Ventas, Madrid',
  image: 'https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/4kxfa3mv_50%20Aniversario_Independencia.jpeg',
  isFree: true,
  externalUrl: 'https://eltrapezio.eu/es/2025/10/25/plaza-de-toros-de-las-ventas-acogera-por-primera-vez-el-dia-de-angola-con-motivo-del-50o-aniversario-de-su-independencia/'
};

export default function Events() {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/events?limit=20').then(r => setEvents(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('events.title')}</h1>
          <p className="text-gray-500 text-sm">{t('events.subtitle')}</p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Featured Event - AngolaDay */}
        <div className="mb-8" data-testid="featured-event">
          <div className="flex items-center gap-2 mb-4">
            <Star size={20} className="text-[#FFD700]" />
            <h2 className="text-xl font-bold text-gray-900">{t('events.featured')}</h2>
          </div>
          <div className="bg-white border-2 border-[#CC0000] rounded-2xl overflow-hidden shadow-lg">
            <div className="md:flex">
              <div className="md:w-1/2 relative">
                <img 
                  src={FEATURED_EVENT.image} 
                  alt={FEATURED_EVENT.title}
                  className="w-full h-64 md:h-full object-cover"
                />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="px-3 py-1 bg-[#CC0000] text-white text-xs font-bold rounded-full">
                    50.o {t('events.anniversary')}
                  </span>
                  {FEATURED_EVENT.isFree && (
                    <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                      {t('common.free_entry')}
                    </span>
                  )}
                </div>
              </div>
              <div className="md:w-1/2 p-6 md:p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <div className="px-3 py-1 bg-[#FFD700] text-[#0A0A0A] text-sm font-bold rounded">
                    11 NOV 2025
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{FEATURED_EVENT.title}</h3>
                <p className="text-gray-600 text-sm mb-4 flex-1">{FEATURED_EVENT.description}</p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock size={16} className="text-[#CC0000]" />
                    <span>{FEATURED_EVENT.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MapPin size={16} className="text-[#CC0000]" />
                    <span>{FEATURED_EVENT.location}</span>
                  </div>
                </div>
                <a 
                  href={FEATURED_EVENT.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#CC0000] text-white font-semibold rounded-xl hover:bg-[#A30000] transition-colors"
                >
                  {t('common.learn_more')} <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Other Events */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t('events.other_events')}</h2>
        </div>
        
        {loading ? (
          <div className="space-y-4">{[...Array(4)].map(i => <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />)}</div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Calendar size={40} className="mx-auto mb-4 opacity-40" />
            <p>{t('events.no_events')}</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="events-list">
            {events.map(evt => {
              const d = new Date(evt.start_date);
              return (
                <div key={evt.id} className="bg-white border border-gray-100 rounded-xl p-5 card-hover flex gap-4" data-testid={`event-${evt.id}`}>
                  <div className="flex-shrink-0 w-14 h-14 bg-[#CC0000] text-white rounded-xl flex flex-col items-center justify-center">
                    <span className="text-xs font-semibold">{d.toLocaleString('pt', {month:'short'}).toUpperCase()}</span>
                    <span className="text-xl font-bold leading-none">{d.getDate()}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{evt.title}</h3>
                    <p className="text-sm text-gray-500 mb-2 line-clamp-2">{evt.description}</p>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock size={11} />
                        {d.toLocaleTimeString('pt', {hour:'2-digit', minute:'2-digit'})}
                        {evt.end_date && ` – ${new Date(evt.end_date).toLocaleTimeString('pt', {hour:'2-digit', minute:'2-digit'})}`}
                      </div>
                      {evt.location && (
                        <div className="flex items-center gap-1">
                          <MapPin size={11} /> {evt.location}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
